import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Field } from "./Register";
import { TID } from "../constants/testIds";
import { toast } from "sonner";

export default function Login() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [accountLang, setAccountLang] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverifiedEmail("");
    setAccountLang(null);
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      navigate(lp("/dashboard"));
      return;
    }
    if (res.unverified) {
      setUnverifiedEmail(res.email);
      setAccountLang(res.language || null);
      localStorage.setItem("lp_pending_email", res.email);
      if (res.language && res.language !== lang) {
        // Reflect the account's language across the app so /check-inbox renders in the right copy.
        localStorage.setItem("lp_lang", res.language);
      }
      return;
    }
    setError(res.error);
  };

  const onResend = async () => {
    const r = await resendVerification(unverifiedEmail || email.trim());
    if (r.ok) {
      toast.success(
        lang === "en" ? "Verification email sent." : "Email de confirmare trimis."
      );
      const targetLang = accountLang || lang;
      navigate(`/${targetLang}/check-inbox`, { state: { email: unverifiedEmail } });
    }
  };

  return (
    <div className="container py-16 md:py-24 max-w-md">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="glass rounded-3xl p-8 md:p-10"
      >
        <h1 className="font-display text-card-lg md:text-section tracking-tight leading-[1.05]">{t.auth.loginTitle}</h1>
        <p className="mt-3 text-cream-muted">{t.auth.loginSub}</p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <Field icon={<Mail className="h-4 w-4 text-cream-dim" />} label={t.auth.email}>
            <input
              data-testid={TID.authEmail}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.auth.email}
              className="input-bare"
            />
          </Field>
          <Field icon={<Lock className="h-4 w-4 text-cream-dim" />} label={t.auth.password}>
            <input
              data-testid={TID.authPassword}
              type={showPw ? "text" : "password"}
              required
              minLength={6}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.auth.password}
              className="input-bare"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              aria-label="toggle password"
              className="text-cream-dim hover:text-cream"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </Field>

          <div className="flex items-center justify-end -mt-2">
            <Link to={lp("/forgot-password")} className="text-caption text-cream-muted hover:text-cream">
              {t.auth.forgotLink}
            </Link>
          </div>

          {unverifiedEmail && (
            <div className="rounded-xl border border-gold/30 bg-gold/8 p-4">
              <p className="text-small text-cream">
                {lang === "en"
                  ? "Your email is not verified yet."
                  : "Emailul tău nu este încă confirmat."}
              </p>
              <button
                type="button"
                onClick={onResend}
                className="btn btn-gold btn-sm mt-3"
                data-testid="login-resend"
              >
                {lang === "en" ? "Resend verification email" : "Retrimite emailul de confirmare"}
              </button>
            </div>
          )}

          {error && (
            <p data-testid={TID.authError} className="text-caption text-red-300/90">
              {error}
            </p>
          )}

          <button
            data-testid={TID.authSubmit}
            type="submit"
            disabled={loading}
            className="btn btn-gold btn-lg w-full disabled:opacity-50"
          >
            {loading ? t.common.loading : t.auth.submitLogin}
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <Link to={lp("/register")} className="block mt-8 text-center text-caption text-cream-muted hover:text-cream">
          {t.auth.switchToRegister}
        </Link>
      </motion.div>
    </div>
  );
}
